import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Icon,
  Flex,
  FormLabel,
  InputGroup,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
  Button,
  Spacer,
} from '@chakra-ui/react';
import { FormikProps, getIn } from 'formik';
import * as _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { compact, pipe, map } from 'remeda';

import {
  checkEligibilityConfigErrors,
  EligibilityFormulaError,
  prettyPrintAllFormulas,
} from '@finley-technologies/calc-agent';
import { FormulaEngine } from '@finley-technologies/finley-types';

import { WipConfig } from '../../types';
import { CreditAgreement, getIterationKey } from '../common';
import { colors } from 'packages/common-frontend/colors';
import { useDraggableRow } from 'packages/common-frontend/components/DragAndDropTable/hooks';
import * as DragAndDropTableTypes from 'packages/common-frontend/components/DragAndDropTable/types';
import { TrDraggableHandle } from 'packages/common-frontend/components/DragAndDropTable/ui';
import { InputFormField } from 'packages/common-frontend/components/FormField';
import { FormulaContainer } from 'packages/common-frontend/components/FormulaContainer';
import { Icon as FinleyIcon } from 'packages/common-frontend/components/icons';
import {
  formatSelectOptions,
  Select,
} from 'packages/common-frontend/components/Select';
import {
  errorToastAdminConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { DeleteArrayPropertyButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeleteArrayPropertyButton';
import { EditButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditButton';
import { EditPropertyModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditPropertyModal';
import { FormulaEditor } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/MonacoEditor/ui/FormulaEditor';
import { FiAlertTriangle, FiCheck } from 'react-icons/fi';

const headers: (keyof CreditAgreement.DomainEntity.EligibilityTestProperty)[] = [
  'name',
  'criteriaNumber',
  'section',
  '_note_',
];

const engineTypeOptions: {
  value: FormulaEngine.SupportedFormulaEngines;
  label: string;
}[] = formatSelectOptions(_.values(FormulaEngine.SupportedFormulaEngines));

export const createEligibilityTestPropertyShell = (): CreditAgreement.DomainEntity.EligibilityTestProperty => ({
  name: 'Placeholder',
  type: CreditAgreement.DomainEntity.PropertyType.EligibilityTestProperty,
  value: 'Placeholder',
  criteriaNumber: 'Placeholder number',
  _isWrittenIn_: true,
  _config_: {},
});

const createEligibilityTestFormulaConfigShell = (
  propertyKey?: string,
): CreditAgreement.Config.EligibilityTest.FormulaTest => ({
  propertyKey: propertyKey || 'placeholder',
  type: 'formula',
  config: {
    formula: '"TODO"',
    engineVersion: FormulaEngine.SupportedFormulaEngines.HyperFormula1,
  },
});

const getFormulaIcon = (errors: EligibilityFormulaError | null) => {
  if (_.isNil(errors)) {
    return <Icon as={FiCheck} color="green.300" />;
  }
  return (
    <Tooltip
      label={
        <Stack>
          {errors.errorMessages.map((e: string) => (
            <Text key={e}>{e}</Text>
          ))}
        </Stack>
      }
      placement="auto-start"
    >
      <Box>
        <Icon as={FiAlertTriangle} color="red.300" />
      </Box>
    </Tooltip>
  );
};

type EligibilityTestPropertyProps = {
  errors: EligibilityFormulaError[];
  // eslint-disable-next-line react/no-unused-prop-types
  headers: (keyof CreditAgreement.DomainEntity.EligibilityTestProperty)[];
  property: CreditAgreement.DomainEntity.EligibilityTestProperty;
  basePropertyKey: string;
  idx: number;
  wipConfig?: WipConfig;
  id: string;
  moveRow: DragAndDropTableTypes.MoveRow;
  isDragDisabled: boolean;
  baseFields: CreditAgreement.DataTapeField.BaseConfig[];
  intermediateCalcs: CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer[];
  currentCommittedEligibilityTestsKeys: string[];
};
const EligibilityTestProperty = ({
  baseFields,
  basePropertyKey,
  errors,
  id,
  idx,
  intermediateCalcs,
  isDragDisabled,
  moveRow,
  property,
  wipConfig,
  currentCommittedEligibilityTestsKeys,
}: EligibilityTestPropertyProps) => {
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);

  const isClientWipMode = wipConfig?.operationMode === 'client';

  const currentError =
    _.find(
      errors,
      ({ propertyKey }) =>
        propertyKey === property._config_?.eligibilityTestConfig?.propertyKey,
    ) || null;

  const eligibilityConfigKey = `${basePropertyKey}[${idx}]._config_.eligibilityTestConfig`;

  const configPrefix = '_config_';
  const eligibilityTestConfigPrefix = 'eligibilityTestConfig';
  const fieldValuePrefix = `${configPrefix}.${eligibilityTestConfigPrefix}`;

  const validateConditionalProperties = useCallback(
    (values: CreditAgreement.DomainEntity.EligibilityTestProperty) =>
      values._config_.eligibilityTestConfig
        ? _.map(
            checkEligibilityConfigErrors({
              eligibilityCalcs: [values._config_.eligibilityTestConfig],
              intermediateCalcs,
              baseFields,
            }),
            (err) => _.join(err.errorMessages, ','),
          )
        : [],
    [baseFields, intermediateCalcs],
  );

  const onAddConditionalProperties = useCallback(
    (localFormProps: FormikProps<any>) => {
      const currentName = localFormProps.values.name;
      const suggestedPropertyKey = _.camelCase(currentName);
      localFormProps.setFieldValue(configPrefix, {
        eligibilityTestConfig: createEligibilityTestFormulaConfigShell(
          suggestedPropertyKey,
        ),
      });
    },
    [configPrefix],
  );

  const onDeleteConditionalProperties = useCallback(
    (localFormProps: FormikProps<any>) => {
      localFormProps.setFieldValue(configPrefix, {});
    },
    [configPrefix],
  );

  const getIsPropertyKeyCurrentlyCommitted = useCallback(
    (localFormProps: FormikProps<any>) => {
      if (
        localFormProps.values[configPrefix]?.[eligibilityTestConfigPrefix]
          ?.propertyKey
      ) {
        return _.includes(
          currentCommittedEligibilityTestsKeys,
          localFormProps.values[configPrefix][eligibilityTestConfigPrefix]
            .propertyKey,
        );
      }
      return false;
    },
    [currentCommittedEligibilityTestsKeys],
  );

  const ConditionalPropertiesComponent = useCallback(
    (localFormProps: FormikProps<any>, validationErrors: Array<string>) => {
      const isPropertyKeyCurrentlyCommitted = getIsPropertyKeyCurrentlyCommitted(
        localFormProps,
      );
      return (
        <>
          {!_.isEmpty(validationErrors) && (
            <Alert status="error" mb="20px">
              <AlertIcon />
              <AlertDescription>
                Errors:
                {validationErrors.join(',')}
              </AlertDescription>
            </Alert>
          )}

          {wipConfig?.isMutateAllowed(eligibilityConfigKey) && (
            <>
              <Tooltip
                label={
                  isClientWipMode && isPropertyKeyCurrentlyCommitted
                    ? 'This eligibility criteria test property key has already been committed and cannot be changed.'
                    : ''
                }
                placement="bottom-start"
              >
                {/* tooltip ref location cannot be determined when directly wrapping a functional component, so adding a span wrapper */}
                <span>
                  <InputFormField
                    propName={`${fieldValuePrefix}.propertyKey`}
                    label="Property Key"
                    formProps={localFormProps}
                    value={
                      localFormProps.values[configPrefix][
                        eligibilityTestConfigPrefix
                      ].propertyKey
                    }
                    isDisabled={
                      isClientWipMode
                        ? isPropertyKeyCurrentlyCommitted
                        : undefined
                    }
                  />
                </span>
              </Tooltip>

              {localFormProps.values[configPrefix][eligibilityTestConfigPrefix]
                .type === 'formula' && (
                <>
                  {!isClientWipMode && (
                    <>
                      <FormLabel mt="20px">Formula Engine</FormLabel>
                      <InputGroup width="100%">
                        <Select
                          value={
                            localFormProps.values[configPrefix][
                              eligibilityTestConfigPrefix
                            ].config.engineVersion
                          }
                          options={engineTypeOptions}
                          onChange={(engineVersion) => {
                            // @ts-ignore
                            const oldConfig: CreditAgreement.Config.EligibilityTest.FormulaConfig =
                              localFormProps.values[configPrefix][
                                eligibilityTestConfigPrefix
                              ].config;
                            const newConfig = {
                              ...oldConfig,
                              engineVersion,
                            };
                            localFormProps.setFieldValue(
                              `${fieldValuePrefix}.config`,
                              newConfig,
                            );
                          }}
                          isDisabled
                        />
                      </InputGroup>
                    </>
                  )}

                  <FormLabel mt="20px" mb="8px">
                    Formula
                  </FormLabel>
                  <Box
                    border="1px"
                    borderColor={colors.gray[200]}
                    borderRadius="8px"
                    p="8px"
                  >
                    <FormulaEditor
                      language="formula-eligibilityTest"
                      value={
                        localFormProps.values[configPrefix][
                          eligibilityTestConfigPrefix
                        ].config.formula
                      }
                      onChange={(value) => {
                        localFormProps.setFieldValue(
                          `${fieldValuePrefix}.config.formula`,
                          value,
                        );
                      }}
                      operationMode={wipConfig.operationMode}
                    />
                  </Box>
                </>
              )}
            </>
          )}
        </>
      );
    },
    [
      fieldValuePrefix,
      wipConfig,
      eligibilityConfigKey,
      isClientWipMode,
      getIsPropertyKeyCurrentlyCommitted,
    ],
  );
  return (
    <>
      <TrDraggableHandle
        index={idx}
        id={id}
        acceptedType="eligibilityTestProperty"
        moveRow={moveRow}
        isDragDisabled={isDragDisabled}
      >
        {_.map(headers, (header: string) => {
          return (
            <Td
              key={`${basePropertyKey}.eligibilityTests[${idx}].${header}`}
              whiteSpace="pre-wrap"
            >
              {
                // @ts-ignore
                property[header] ? property[header] : '-'
              }
            </Td>
          );
        })}

        {(() => {
          if (property._config_?.eligibilityTestConfig?.type === 'formula') {
            return (
              <>
                <Td>{getFormulaIcon(currentError)}</Td>
                <Td whiteSpace="pre-wrap">
                  <FormulaContainer>
                    {property._config_?.eligibilityTestConfig?.config.formula}
                  </FormulaContainer>
                </Td>
              </>
            );
          }

          return (
            <>
              <Td />
              <Td />
            </>
          );
        })()}

        {wipConfig?.isMutateAllowed(`${basePropertyKey}[${idx}]`) &&
          isDragDisabled && (
            <Td>
              <EditButton setIsOpen={setIsEditOpen} />
              <EditPropertyModal<CreditAgreement.DomainEntity.EligibilityTestProperty>
                isOpen={isEditOpen}
                setIsOpen={setIsEditOpen}
                wipConfig={wipConfig}
                basePropertyKey={`${basePropertyKey}[${idx}]`}
                modalHeaderLabel="Eligibility Test"
                conditionalProperties={{
                  conditionalPropertiesComponent: ConditionalPropertiesComponent,
                  conditionalPropertiesToggleLabel: 'Configuration',
                  conditionalPropertiesValidation: validateConditionalProperties,
                  conditionalPropertiesOnSubmitTransform: (values) =>
                    prettyPrintAllFormulas(values),
                  useConditionalProperties: !!property._config_
                    ?.eligibilityTestConfig,
                  onDeleteConditionalProperties: (
                    localFormProps: FormikProps<any>,
                  ) => onDeleteConditionalProperties(localFormProps),
                  onAddConditionalProperties: (
                    localFormProps: FormikProps<any>,
                  ) => onAddConditionalProperties(localFormProps),
                  getIsPropertyKeyCurrentlyCommitted: (
                    localFormProps: FormikProps<any>,
                  ) => getIsPropertyKeyCurrentlyCommitted(localFormProps),
                }}
              >
                {(localFormProps) => (
                  <>
                    <InputFormField
                      propName="value"
                      label="Value"
                      formProps={localFormProps}
                      value={localFormProps.values.value}
                    />
                    <InputFormField
                      propName="criteriaNumber"
                      label="Criteria Number"
                      formProps={localFormProps}
                      value={localFormProps.values.criteriaNumber}
                    />
                  </>
                )}
              </EditPropertyModal>
              <DeleteArrayPropertyButton
                wipConfig={wipConfig}
                basePropertyKey={basePropertyKey}
                idx={idx}
              />
            </Td>
          )}
      </TrDraggableHandle>
    </>
  );
};

type LoanProductEligibilityTestsProps = {
  baseFields: CreditAgreement.DataTapeField.BaseConfig[];
  intermediateCalcs: CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer[];
  eligibilityTests: CreditAgreement.DomainEntity.EligibilityTestProperty[];
  basePropertyKey: string;
  idx: number;
  wipConfig?: WipConfig;
  currentCommittedEligibilityTestsKeys: string[];
};
export const LoanProductEligibilityTests = ({
  baseFields,
  eligibilityTests,
  intermediateCalcs,
  basePropertyKey,
  wipConfig,
  idx,
  currentCommittedEligibilityTestsKeys,
}: LoanProductEligibilityTestsProps) => {
  const toast = useToast();
  const [errors, setCurrentErrors] = useState<EligibilityFormulaError[]>([]);
  const {
    useRowState,
    useOrderLockedState,
    moveRow,
    ui: { LockOrderButton, RestoreOrderButton, SaveOrderCallout },
  } = useDraggableRow<CreditAgreement.DomainEntity.EligibilityTestProperty>({
    comparisonKey: 'name',
  });
  const [isOrderLocked] = useOrderLockedState();
  const [eligibilityTestsState] = useRowState(eligibilityTests);

  const handleAdd = () => {
    const propertyKey = `${basePropertyKey}[${idx}].eligibilityTests`;
    const newEligibilityTest = createEligibilityTestPropertyShell();
    const previousEligibilityTests = getIn(
      wipConfig?.formProps.values,
      propertyKey,
    );
    wipConfig?.formProps.setFieldValue(propertyKey, [
      ...previousEligibilityTests,
      newEligibilityTest,
    ]);
  };

  const checkForCalcErrors = useCallback(
    (eligibilityCalcs: any) => {
      const checkErrors: EligibilityFormulaError[] = checkEligibilityConfigErrors(
        {
          eligibilityCalcs,
          baseFields,
          intermediateCalcs: intermediateCalcs as CreditAgreement.Config.LoanTapeField.IntermediateCalcs.IntermediateCalcContainer[],
        },
      );

      if (checkErrors.length && !toast.isActive(errorToastId)) {
        const description = checkErrors.map((err) => (
          <Text key={err.propertyKey}>
            {err.propertyKey} errors: [{err.errorMessages.join(', ')}]
            {JSON.stringify(err)}
          </Text>
        ));
        errorToastAdminConfig.title = `There Are Errors In The Config Formula`;
        toast({ ...errorToastAdminConfig, description });
      }

      setCurrentErrors(checkErrors);
    },
    [baseFields, intermediateCalcs, toast, setCurrentErrors],
  );

  const onSaveOrder = useCallback(() => {
    wipConfig?.formProps.setFieldValue(
      `borrowingBaseMechanics.calcAgentMechanics.loanProductMechanics[${idx}].eligibilityTests`,
      eligibilityTestsState,
    );
  }, [wipConfig, idx, eligibilityTestsState]);

  useEffect(() => {
    checkForCalcErrors(
      pipe(
        eligibilityTests,
        map(({ _config_ }) => _config_?.eligibilityTestConfig),
        compact,
      ),
    );
  }, [
    eligibilityTests,
    baseFields,
    intermediateCalcs,
    checkForCalcErrors,
    intermediateCalcs,
  ]);

  return (
    <Box mb="35px" width="100%">
      <Flex mb="10px" alignItems="center">
        {wipConfig?.isMutateAllowed(
          `${basePropertyKey}[${idx}].eligibilityTests`,
        ) && (
          <LockOrderButton
            onSaveOrder={onSaveOrder}
            isDisabled={
              !wipConfig.isMutateAllowed(
                `${basePropertyKey}[${idx}].eligibilityTests`,
              )
            }
          />
        )}
        <Text fontWeight="bold" fontSize="20px">
          Eligibility Tests
        </Text>
        {wipConfig?.isMutateAllowed(
          `${basePropertyKey}[${idx}].eligibilityTests`,
        ) && <RestoreOrderButton />}
        {wipConfig?.isMutateAllowed(
          `${basePropertyKey}[${idx}].eligibilityTests`,
        ) && (
          <>
            <Spacer />
            <Button
              aria-label="Add Eligibility Test"
              minW="fit-content"
              borderWidth="2px"
              colorScheme="v2-aurora"
              variant="secondary"
              onClick={() => handleAdd()}
              isDisabled={!isOrderLocked}
            >
              <FinleyIcon icon="plus" />
              <Text ml="16px">Add Eligibility Test</Text>
            </Button>
          </>
        )}
      </Flex>
      {wipConfig?.isMutateAllowed(
        `${basePropertyKey}[${idx}].eligibilityTests`,
      ) && <SaveOrderCallout />}
      {!_.isEmpty(eligibilityTestsState) ? (
        <Flex width="100%" overflowY="auto">
          <Table borderWidth="1px">
            <Thead>
              <Tr>
                {wipConfig && !isOrderLocked && <Th />}
                {headers.map((header) => (
                  <Th
                    key={`${basePropertyKey}[${idx}]-header-${header}`}
                    minWidth={header === 'name' ? '15rem' : '10rem'}
                  >
                    {_.startCase(header)}
                  </Th>
                ))}
                <Th width="6rem" minWidth="6rem">
                  Valid
                </Th>
                <Th width="25rem" minWidth="25rem">
                  Formula
                </Th>
                {wipConfig && isOrderLocked && (
                  <Th width="8.5rem" minWidth="8.5rem" />
                )}
              </Tr>
            </Thead>
            <Tbody>
              {eligibilityTestsState.map((eligibilityTest, testIdx) => {
                const id = getIterationKey(eligibilityTest);
                return (
                  <EligibilityTestProperty
                    errors={errors}
                    key={id}
                    id={_.camelCase(eligibilityTest.name)}
                    moveRow={moveRow}
                    headers={headers}
                    property={eligibilityTest}
                    wipConfig={wipConfig}
                    basePropertyKey={`${basePropertyKey}[${idx}].eligibilityTests`}
                    idx={testIdx}
                    isDragDisabled={isOrderLocked}
                    intermediateCalcs={intermediateCalcs}
                    baseFields={baseFields}
                    currentCommittedEligibilityTestsKeys={
                      currentCommittedEligibilityTestsKeys
                    }
                  />
                );
              })}
            </Tbody>
          </Table>
        </Flex>
      ) : (
        <Text>There are no eligibility tests currently configured</Text>
      )}
    </Box>
  );
};
